<template>
  <div>
    <v-alert color="var(--red-100)" v-if="isSubmitError" class="error-message">
      <div class="d-flex flex-no-wrap">
        <div class="mr-3">
          <v-icon color="var(--red-500)">mdi-alert-circle</v-icon>
        </div>
        <div v-html="submitErrorMessage"></div>
      </div>
    </v-alert>
    <div class="d2c-journey__container d2c-payment__container">
      <div class="white rounded-lg elevation-3 pa-4 pa-xl-16">
        <BaseButton
          :text="LABEL_CONSTANTS.BACK_TO_YOUR_APPLICATIONS"
          flat
          ripple
          class="font-weight-semi-bold d-flex back-btn back-previous__btn-link pa-0 mb-5"
          icon="mdi-chevron-left"
          color="#106175"
          small
          @click="backToYourApplication"
        />
        <v-row class="mb-3">
          <v-col class="d-flex justify-space-between flex-wrap section-title">
            <div>
              <h1 class="font-28 font-weight-bold black2-color">Payment</h1>
            </div>
            <BaseButton
              text="Save &amp; exit"
              flat
              depressed
              ripple
              class="font-weight-semi-bold d2c-save-exit mt-2 mr-2 py-3 d-flex"
              icon="mdi-content-save"
              color="#106175"
              small
              @click="
                toggleGlobalSaveAndExitDialog(
                  EXIT_DESTINATION.landlordWelcomePage
                )
              "
            />
          </v-col>
        </v-row>
        <ValidationObserver
          class="payment-form"
          ref="observerFormPaymentDetails"
          v-slot="{ validate, invalid }"
        >
          <v-form
            ref="form"
            @submit.prevent="validate().then(() => onSubmit(invalid))"
          >
            <BaseLabel
              :text="
                $vuetify.breakpoint.lgAndUp
                  ? LABEL_CONSTANTS.PAYMENT_METHOD_LABEL
                  : 'Payment method'
              "
            />
            <BaseRadio
              className="radio-payment-method mb-8"
              :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
              :width="$vuetify.breakpoint.smAndDown ? '160' : '248'"
              v-model="questionPaymentMethod.Value"
              pslStyle="button"
              :items="paymentMethodOptions"
              :rules="{
                required: {
                  required: true,
                  fieldLabel: LABEL_CONSTANTS.PAYMENT_METHOD_LABEL,
                },
              }"
              :data-uiid="questionPaymentMethod.InterfaceKey"
            />

            <div v-if="questionPaymentMethod.Value === 'DirectDebit'">
              <BaseLabel :text="LABEL_CONSTANTS.FREQUENTLY_PAYMENT" />
              <BaseSelect
                v-model="questionPaymentFrequency.Value"
                :data-uiid="questionPaymentFrequency.InterfaceKey"
                :hide-details="false"
                :rules="paymentFrequencyRules"
                :items="frequencyPaymentOptions"
                :selectedDefault="LABEL_CONSTANTS.SELECT_DEFAULT_PLACEHOLDER"
                className="mb-4 frequently-payment__select"
                :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
              />
            </div>

            <TheSelectedQuote
              :paymentFrequency="applicationPaymentFrequency"
              :cover="getCoverLandlords"
              :insurer="insurer"
              :isPaymentPage="true"
              :quoteExcessConfiguration="
                getQuoteExcessConfiguration?.LandlordsInsurance
              "
              @showD2CPremiumBreakdown="showD2CPremiumBreakdown"
            />

            <div v-if="questionPaymentMethod.Value === 'DirectDebit'">
              <div
                v-if="
                  questionPaymentMethod.Value === 'DirectDebit' &&
                  questionPaymentFrequency.Value === 'Monthly'
                "
              >
                <v-alert color="var(--blue-100)" class="mt-10 mb-0">
                  <div class="d-flex flex-no-wrap">
                    <div class="mr-3">
                      <v-icon color="var(--blue-500)">mdi-information</v-icon>
                    </div>
                    <div>
                      <span class="font-weight-bold">Important!</span> By
                      selecting the monthly payment option you've chosen to
                      enter into a credit agreement. Failure to keep up with
                      payments is a breach of this agreement and may result in
                      the cancellation of your insurance policy. Please tick the
                      box below to confirm that the repayments are affordable
                      and you're happy to enter into a credit agreement. If you
                      feel the payments may not be affordable, please consider
                      other payment options.
                    </div>
                  </div>
                </v-alert>
                <BaseCheckBox
                  :checked="questionRepaymentConfirmation.Value"
                  :rules="repaymentConfirmationRules"
                  :name="questionRepaymentConfirmation.InterfaceKey"
                  :hide-details="false"
                  className="repayment-confirmation__checkbox font-weight-bold"
                  label="I confirm that I have read, understood and agreed to the above statement."
                  @change="(val) => (questionRepaymentConfirmation.Value = val)"
                />
              </div>

              <div
                :class="
                  questionPaymentFrequency.Value === 'Monthly' ? 'mt-3' : 'mt-9'
                "
              >
                <BaseLabel
                  :text="LABEL_CONSTANTS.PAYMENT_DETAILS"
                  label-class="mb-2 font-20 font-weight-bold main-label__wrapper"
                />
              </div>
              <BaseAlert
                :alertLabel="LABEL_CONSTANTS.ALERT_PAYMENT_SECURE"
                alertTextClass="font-18 black2-color"
                :max-width="$vuetify.breakpoint.lgAndUp ? '571px' : '100%'"
                icon="mdi-lock"
                color="#EBF5FD"
                alertBorder
                alertBorderColor="#5285C0"
                alertBorderPosition="left"
                prominent
                prominentColor="#5285C0"
                prominentIconColor="white"
                prominentIconSize="19"
              />

              <BaseLabel
                :text="LABEL_CONSTANTS.ACCOUNT_HOLDER_NAME"
                label-class="mt-8 font-18 font-weight-medium main-label__wrapper"
              />
              <BaseInput
                vid="BankAccountName"
                v-model="questionBankAccountName.Value"
                :data-uiid="questionBankAccountName.InterfaceKey"
                :hide-details="false"
                :rules="bankAccountNameRules"
                className="long-value__input"
                :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
              />

              <BaseLabel :text="LABEL_CONSTANTS.SORT_CODE" />
              <BaseInput
                vid="BankSortCode"
                v-model="questionBankSortCode.Value"
                :data-uiid="questionBankSortCode.InterfaceKey"
                :hide-details="false"
                :rules="bankSortCodeRules"
                mask="##-##-##"
                className="short-value__input"
                :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
              />

              <BaseLabel :text="LABEL_CONSTANTS.ACCOUNT_NUMKBER" />
              <BaseInput
                vid="BankAccountNo"
                v-model="questionBankAccountNo.Value"
                :data-uiid="questionBankAccountNo.InterfaceKey"
                :hide-details="false"
                :rules="bankAccountNoRules"
                className="short-value__input"
                :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
              />

              <BaseLabel :text="LABEL_CONSTANTS.DIRECT_DEBIT_DATE" />
              <BaseSelect
                v-model="questionDirectDebitDate.Value"
                :data-uiid="questionDirectDebitDate.InterfaceKey"
                :hide-details="false"
                :rules="directDebitDateRules"
                :items="directDebitDateOptions"
                :isEnableSortOrderListItems="false"
                :selectedDefault="LABEL_CONSTANTS.SELECT_DEFAULT_PLACEHOLDER"
                className="long-value__input"
                :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
              />

              <BaseLabel
                :text="LABEL_CONSTANTS.STAY_COVERED"
                label-class="mb-1 font-20 font-weight-bold main-label__wrapper"
              />
              <BaseRadio
                v-model="questionAutoRenewalConsent.Value"
                :data-uiid="questionAutoRenewalConsent.InterfaceKey"
                pslStyle="card"
                :arrangeCoverDescription="ARRANGE_COVER_DESCRIPTION"
              />
            </div>
            <div v-else>
              <v-alert color="var(--blue-100)" class="mt-10 mb-0">
                <div class="d-flex flex-no-wrap">
                  <div class="mr-3">
                    <v-icon color="var(--blue-500)">mdi-information</v-icon>
                  </div>
                  <div>
                    <p>
                      <span class="font-weight-bold">Important!</span>&nbsp;As
                      you've chosen to pay by annual credit/debit card, you've
                      been opted out of our annual renewal service. If you'd
                      prefer to opt into our auto-renewal service, please change
                      your payment method to
                      <strong>Direct Debit</strong> above.
                    </p>
                    <p>
                      If you remain opted out of our automatic renewal service,
                      please make sure you have adequate insurance cover in
                      place once your Paymentshield policy comes to an end.
                    </p>
                    <p>
                      If you own a home with a mortgage, it's often a condition
                      of your mortgage agreement to maintain continuous
                      buildings insurance to protect your home against serious
                      damage such as fire or flood.
                    </p>
                  </div>
                </div>
              </v-alert>
            </div>
            <div class="mt-10">
              <BaseButton
                :text="
                  questionPaymentMethod.Value === 'DirectDebit'
                    ? 'PAY NOW'
                    : 'CONTINUE TO PAYMENT'
                "
                rounded
                :class="'font-weight-bold continue-button'"
                height="60"
                icon="mdi-lock"
                iconColor="#272727"
                iconSize="20px"
                color="psorange"
                textColor="black2-color"
                fontSize="font-20"
                :loading="isApplying"
                type="submit"
                :min-width="
                  $vuetify.breakpoint.lgAndUp
                    ? questionPaymentMethod.Value === 'DirectDebit'
                      ? '261'
                      : '373'
                    : '100%'
                "
              />
            </div>
          </v-form>
        </ValidationObserver>
      </div>
    </div>
    <BaseDialog
      ref="D2CDialogContentPremiumBreakdown"
      windowClass="pa-0 pa-xl-4 pb-xl-0"
      headerClass="pa-4 d2c-dialog-header"
      contentClass="d2c-breakdown-content"
      dark2Icon
    >
      <template v-slot:content="{ data }">
        <D2CDialogContentPremiumBreakdown
          :breakdownModel="data"
        ></D2CDialogContentPremiumBreakdown>
      </template>
    </BaseDialog>
  </div>
</template>

<script>
import {
  scrollTop,
  addFieldLabel,
  convertToBool,
  getErrorMessage,
} from "@/common/utils";
import { EventBus } from "@/common/eventbus.js";
import { ValidationObserver } from "vee-validate";
import { mapActions, mapGetters, mapState } from "vuex";
import PremiumBreakdownMixin from "@/Mixins/PremiumBreakdownMixin";
import BuildingsContentsVisibility from "@/Mixins/QuoteResults/BuildingsContentsVisibility";

import {
  QUESTIONS_MODULE,
  APPLICATION_MODULE,
} from "@/store/types/module-types";
import {
  setItemDataUiids,
  setItemTextDevice,
} from "@/constants/component.constants";
import {
  ARRANGE_COVER_DESCRIPTION,
  DIRECT_DEBIT_DATE_ITEMS,
  FREQUENTLY_PAYMENT_ITEMS,
  PAYMENT_METHOD_ITEMS,
} from "@/constants/payment.constants";
import {
  ERROR_TEXT_ONLY,
  ERROR_NUMERICAL_ONLY,
  ERROR_MSG_MAX_CHARACTER,
  ERROR_MAX_LENGTH_SORT_CODE,
  ERROR_MIN_LENGTH_SORT_CODE,
} from "@/constants/message.constants";
import {
  SORT_CODE_REGEX,
  TEXT_ONLY_REGEX,
  NUMBER_ONLY_REGEX,
} from "@/constants/regex-patterns.constants";
import { ROUTE_NAME } from "@/constants/route.constants";
import * as LABEL_CONSTANTS from "@/constants/label.constants";
import { EXIT_DESTINATION } from "@/constants/common.constants";

import BaseAlert from "@/components/BaseComponents/BaseAlert.vue";
import BaseButton from "@/components/BaseComponents/BaseButton.vue";
import BaseCheckBox from "@/components/BaseComponents/BaseCheckBox.vue";
import BaseInput from "@/components/BaseComponents/BaseInput.vue";
import BaseLabel from "@/components/BaseComponents/BaseLabel.vue";
import BaseRadio from "@/components/BaseComponents/BaseRadio.vue";
import BaseSelect from "@/components/BaseComponents/BaseSelect.vue";
import TheSelectedQuote from "@/components/D2CSharedComponents/TheSelectedQuote.vue";

export default {
  name: "D2CLandlordPaymentPage",
  components: {
    BaseLabel,
    BaseButton,
    BaseRadio,
    BaseAlert,
    BaseInput,
    BaseSelect,
    BaseCheckBox,
    ValidationObserver,
    TheSelectedQuote,
    BaseDialog: () => import("@/components/BaseComponents/BaseDialog.vue"),
    D2CDialogContentPremiumBreakdown: () =>
      import("@/components/QuoteResult/D2CDialogContentPremiumBreakdown.vue"),
  },
  mixins: [PremiumBreakdownMixin, BuildingsContentsVisibility],
  data() {
    return {
      LABEL_CONSTANTS,
      isApplying: false,
      isSubmitError: false,
      submitErrorMessage: "",
      questionPaymentMethod: {},
      questionPaymentFrequency: {},
      questionBankAccountName: {},
      questionBankSortCode: {},
      questionBankAccountNo: {},
      questionDirectDebitDate: {},
      questionAutoRenewalConsent: {},
      questionRepaymentConfirmation: {
        Value: false,
        InterfaceKey: "RepaymentConfirmation",
      },
      PAYMENT_METHOD_ITEMS,
      DIRECT_DEBIT_DATE_ITEMS,
      FREQUENTLY_PAYMENT_ITEMS,
      EXIT_DESTINATION,
      ARRANGE_COVER_DESCRIPTION,
      paymentMethodRules: addFieldLabel(
        {
          required: {
            required: true,
          },
        },
        "Payment method"
      ),
      bankAccountNameRules: addFieldLabel(
        {
          required: {
            required: true,
          },
          max: {
            length: 30,
            errorMessageTemplate: ERROR_MSG_MAX_CHARACTER,
          },
          regex: {
            errorMessageTemplate: ERROR_TEXT_ONLY,
            pattern: TEXT_ONLY_REGEX,
          },
        },
        "Account holder name"
      ),
      bankSortCodeRules: addFieldLabel(
        {
          required: {
            required: true,
          },
          regex: {
            errorMessageTemplate: ERROR_NUMERICAL_ONLY,
            pattern: SORT_CODE_REGEX,
          },
          max: {
            length: 8,
            errorMessageTemplate: ERROR_MAX_LENGTH_SORT_CODE,
          },
          min_length: {
            length: 8,
            errorMessageTemplate: ERROR_MIN_LENGTH_SORT_CODE,
          },
        },
        "Sort code"
      ),
      bankAccountNoRules: addFieldLabel(
        {
          required: {
            required: true,
          },
          max: {
            length: 8,
            errorMessageTemplate: ERROR_MSG_MAX_CHARACTER,
          },
          min_length: {
            length: 8,
          },
          regex: {
            errorMessageTemplate: ERROR_NUMERICAL_ONLY,
            pattern: NUMBER_ONLY_REGEX,
          },
        },
        "Account number"
      ),
      directDebitDateRules: addFieldLabel(
        {
          required: {
            required: true,
          },
        },
        "Preferred DD date"
      ),
    };
  },
  mounted() {
    scrollTop();
    this.onSetupAndLoadSavedData();
    this.setRestoringLastStep(false);
    this.setCurrentRefObserverForm(this.$refs.observerFormPaymentDetails);
  },
  computed: {
    ...mapState(APPLICATION_MODULE, [
      "activeQuote",
      "questionMap",
      "applyResponse",
    ]),
    ...mapGetters(QUESTIONS_MODULE, [
      "getCoverLandlords",
      "getQuoteExcessConfiguration",
    ]),
    insurer() {
      return this.activeQuote;
    },
    paymentMethodOptions() {
      setItemTextDevice(
        this.PAYMENT_METHOD_ITEMS,
        this.$vuetify.breakpoint.mdAndDown
      );
      return setItemDataUiids("PaymentMethod", this.PAYMENT_METHOD_ITEMS);
    },
    frequencyPaymentOptions() {
      return setItemDataUiids(
        "PaymentFrequency",
        this.FREQUENTLY_PAYMENT_ITEMS
      );
    },
    directDebitDateOptions() {
      return setItemDataUiids("DirectDebitDate", this.DIRECT_DEBIT_DATE_ITEMS);
    },
    applicationPaymentFrequency() {
      if (this.questionPaymentMethod.Value === "CreditCard") {
        return "Annual";
      }
      return this.questionMap.PaymentFrequency?.Value;
    },
    isPaymentMethodDirectDebit() {
      return this.questionPaymentMethod.Value === "DirectDebit";
    },
    isPaymentFrequencyMonthly() {
      return this.questionPaymentFrequency.Value === "Monthly";
    },
    paymentFrequencyRules() {
      return !this.isPaymentMethodDirectDebit
        ? null
        : addFieldLabel(
            {
              required: {
                required: true,
              },
            },
            "Payment frequency"
          );
    },
    repaymentConfirmationRules() {
      return !this.isPaymentFrequencyMonthly || !this.isPaymentMethodDirectDebit
        ? null
        : addFieldLabel(
            {
              required: {
                required: true,
              },
            },
            "Repayment Confirmation"
          );
    },
  },
  watch: {
    "questionRepaymentConfirmation.Value"(value) {
      if (value) {
        this.questionMap.RepaymentConfirmation.Value = true;
      } else {
        this.questionMap.RepaymentConfirmation.Value = "";
      }
    },
  },
  methods: {
    ...mapActions(QUESTIONS_MODULE, [
      "saveProgress",
      "toggleGlobalSaveAndExitDialog",
      "setCurrentRefObserverForm",
      "setRestoringLastStep",
    ]),
    ...mapActions(APPLICATION_MODULE, [
      "setCardPaymentStatus",
      "handleApplicationSubmission",
      "proceedToCardPayment",
      "setSessionStorageItems",
    ]),

    submitDirectDebitApplication() {
      this.isSubmitError = false;
      this.isApplying = true;
      this.handleApplicationSubmission()
        .then(() => {
          this.setSessionStorageItems();
          this.isApplying = false;
          this.isSubmitError = false;
          this.$router.push({
            name: ROUTE_NAME.LANDLORDS.D2C.COMPLETE,
          });
        })
        .catch((error) => {
          this.isSubmitError = true;
          this.isApplying = false;
          this.submitErrorMessage = getErrorMessage(error);
          console.log("Apply error:", error);
        });
    },

    submitCardPaymentApplication() {
      this.isApplying = true;
      this.isSubmitError = false;
      this.handleApplicationSubmission()
        .then(() => {
          this.setSessionStorageItems();
          this.proceedToCardPayment();
        })
        .catch((error) => {
          this.isSubmitError = true;
          this.submitErrorMessage = getErrorMessage(error);
          this.isApplying = false;
          console.log("Apply error:", error);
        });
    },
    backToYourApplication() {
      this.$router.push({ name: ROUTE_NAME.LANDLORDS.D2C.APPLICATION });
    },
    loadSaveAndExitDialog() {
      alert("Save and Exit Dialog");
    },
    async onSubmit(invalid) {
      if (invalid) {
        EventBus.$emit("validation-error", "Validation error occurred");
        return;
      }
      this.setCardPaymentStatus("");
      if (this.questionPaymentMethod.Value === "DirectDebit") {
        this.submitDirectDebitApplication();
      } else {
        this.questionMap.PaymentFrequency.Value = "Annual";
        this.submitCardPaymentApplication();
      }
    },
    onSetupAndLoadSavedData() {
      this.questionPaymentMethod = this.questionMap.PaymentMethod;
      this.questionPaymentFrequency = this.questionMap.PaymentFrequency;
      this.questionBankAccountName = this.questionMap.BankAccountName;
      this.questionBankSortCode = this.questionMap.BankSortCode;
      this.questionBankAccountNo = this.questionMap.BankAccountNo;
      this.questionDirectDebitDate = this.questionMap.DirectDebitDate;
      this.questionAutoRenewalConsent = this.questionMap.AutoRenewalConsent;
      this.questionAutoRenewalConsent.Value = convertToBool(
        this.questionAutoRenewalConsent.Value
      );
      if (this.questionMap.RepaymentConfirmation.Value) {
        this.questionRepaymentConfirmation.Value = true;
      }
    },
    cancelMissingBankDetails() {
      this.$refs.directDebitMissingBankDetails.cancel();
    },
    handleMissingBankDetails() {
      this.$refs.directDebitMissingBankDetails.cancel();
      this.submitDirectDebitApplication();
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/scss/d2c/journey.scss";
.error-message {
  border: solid 2px var(--v-error-base) !important;
  margin: 0px 14px 16px 14px;
}
.d2c-journey__container {
  padding: 0 !important;
  .select__wrapper {
    margin-bottom: 12px;
    max-width: 100%;
    :deep(.v-select__selection),
    :deep(.v-label) {
      color: #000000;
      font-size: 18px;
    }
  }
  :deep(.main-label__wrapper) {
    padding-bottom: 0 !important;
  }
}
.d2c-payment__container {
  color: #272727;
  :deep(.v-alert) {
    border-radius: 0px !important;
    padding-left: 20px !important;
  }
  .continue-button {
    :deep(.icon__wrap) {
      margin-right: 18px !important;
    }
  }
  .repayment-confirmation__checkbox {
    padding-left: 3px;
    :deep(.v-input__slot) {
      margin-bottom: 0px;
    }
    :deep(.v-label) {
      font-size: 18px;
      color: #272727;
    }
    :deep(.v-input__slot) {
      min-height: 35px !important;
    }
    :deep(.v-icon) {
      font-size: 32px;
    }
    :deep(.v-messages) {
      margin-left: -3px;
    }
  }
  .radio-payment-method {
    :deep(.v-input--radio-group__input) {
      button {
        margin-right: 16px !important;
      }
    }
  }
  :deep(.v-text-field__details) {
    padding-left: 0 !important;
    position: unset;
  }
}
@media screen and (min-width: $grid-breakpoints-lg) {
  .error-message {
    margin-left: 0px;
    margin-right: 0px;
  }
  .d2c-payment__container {
    .continue-button {
      :deep(.icon__wrap) {
        margin-right: 14px !important;
      }
    }
    .frequently-payment__select {
      max-width: 571px !important;
    }
  }
  .long-value__input {
    max-width: 489px !important;
  }
  .short-value__input {
    max-width: 219px !important;
  }
}
</style>
